import ColorUtils from "./ColorUtils";
import {LayerRole} from "qwc2/actions/layers";
import ConfigUtils from "qwc2/utils/ConfigUtils";
import ol from "openlayers";
import Moment from "moment";
import * as uuid from 'uuid';

const IndicatorUtils = {

    getUrl() {
        return ConfigUtils.getConfigProp("indicatorsServiceUrl");
    },

    normalizeDate(date) {
        return date.substring(0, 10);
    },

    getDefaultDate(indicator) {
        const time = this.getTimeSlices(indicator)[0];
        return this.normalizeDate(time);
    },

    getDevelopmentUrl(indicator, objectType, date) {
        let url = this.getUrl() + 'indicator/' + indicator.id + '/objectname/' + objectType;

        if (date) {
            url += '?filter=date=\'' + this.normalizeDate(date) + '\'';
        }

        return url;
    },

    getDataUrl(indicator, objectType, date, uuids) {
        let url = this.getUrl() + 'collections/indicatorvalue/items/?limit=500&filter=indicator=\'' + indicator.id + '\' and linkedObjectType=\'' + objectType + '\'';

        if (date) {
            url += ' and date=\'' + this.normalizeDate(date) + '\' ';
        }

        if (uuids && uuids.length && uuids.length > 0) {
            const uidstrings = [];
            uuids.forEach(u => uidstrings.push('\'' + u + '\''));
            url += ' and linkedObjectUuid IN (' + uidstrings.join(',') + ')';
        }

        return url;
    },

    getSld(indicator) {
        let legendUrl = this.getLegendUrl(indicator);
        if (legendUrl.endsWith('.png')) {
            legendUrl = legendUrl.substring(0, legendUrl.lastIndexOf('.png')) + '.sld';
        }
        return legendUrl;
    },

    getLegendImage(indicator) {
        const legendUrl = this.getLegendUrl(indicator);
        if (legendUrl.endsWith('.png')) {
            return legendUrl;
        }
        return null;
    },

    getLegendUrl(indicator) {
        let sld = indicator.legend;
        if (sld && sld.indexOf('http') !== 0) {
            const repositoryUrl = ConfigUtils.getConfigProp("repositoryServiceUrl");
            const urlParts = repositoryUrl.split('/');
            urlParts.pop();
            urlParts.pop();
            sld = urlParts.join('/') + '/sld/' + sld;
        }
        console.log('SLD', sld);
        return sld;
    },

    getLayer(indicator, objectType, date) {
        if (!date) {
            date = this.getDefaultDate(indicator);
        }
        const url = this.getDataUrl(indicator, objectType, date);

        const id = indicator.id + '-' + objectType + '-' + date + '-indlr';


        const layer = {
            title: indicator.name,
            name: indicator.name,
            id: id,
            uuid: uuid.v4(),
            active: true,
            visibility: true,
            role: LayerRole.USERLAYER,
            expanded: true,
            type: 'geojson',
            drawingOrder: 50,
            zoomToExtent: false,
            queryable: false,
            opacity: 255,
            projection: "EPSG:4326",
            url: url,
        };

        const sld = this.getSld(indicator);
        if (sld) {
            layer.layout = sld;
        } else {
            layer.layoutRanges = IndicatorUtils.ranges(indicator);
            layer.layoutFunction = function (feature, options) {
                //console.log('Options', feature.get('value'), options);
                const color = ColorUtils.hexToRgb(IndicatorUtils.getColor(options.layoutRanges, feature.get('value')));
                //console.log('Color', feature.get('value'), color);
                const opts = {
                    strokeColor: [0, 0, 255, 1],
                    strokeWidth: 1,
                    strokeDash: [4],
                    fillColor: [255, 0, 255, 0.33],
                    circleRadius: 10,
                    textFill: "black",
                    textStroke: "white",
                    textFont: "11pt sans-serif"
                };
                opts.fillColor = [color.r, color.g, color.b, 0.5];
                opts.strokeColor = [color.r, color.g, color.b, 1];
                return [
                    new ol.style.Style({
                        fill: new ol.style.Fill({
                            color: opts.fillColor
                        }),
                        stroke: new ol.style.Stroke({
                            color: opts.strokeColor,
                            width: opts.strokeWidth,
                            lineDash: opts.strokeDash
                        }),
                        image: opts.circleRadius > 0 ? new ol.style.Circle({
                            radius: opts.circleRadius,
                            fill: new ol.style.Fill({color: opts.fillColor}),
                            stroke: new ol.style.Stroke({color: opts.strokeColor, width: opts.strokeWidth})
                        }) : null
                    }),
                    new ol.style.Style({
                        geometry: (f) => {
                            if (f.getGeometry().getType().startsWith("Multi")) {
                                // Only label middle point
                                const extent = f.getGeometry().getExtent();
                                return new ol.geom.Point(f.getGeometry().getClosestPoint(ol.extent.getCenter(extent)));
                            }
                            return f.getGeometry();
                        },
                        text: new ol.style.Text({
                            font: opts.textFont || '11pt sans-serif',
                            text: feature.getProperties().label || "",
                            overflow: true,
                            fill: new ol.style.Fill({color: opts.textFill}),
                            stroke: new ol.style.Stroke({color: opts.textStroke, width: 3}),
                            textAlign: feature.getGeometry().getType() === "Point" ? 'left' : 'center',
                            textBaseline: feature.getGeometry().getType() === "Point" ? 'bottom' : 'middle',
                            offsetX: feature.getGeometry().getType() === "Point" ? (5 + opts.circleRadius) : 0
                        })
                    })
                ];
            };
        }

        return layer;
    },

    ranges(indicator) {
        const count = 5;
        const baseColor = '#00008b';

        const min = indicator.minValue;
        const max = indicator.maxValue;

        const stepsSize = (max - min) / count;
        const brigthen = 100 / count;

        const ranges = [];
        for (let i = 0; i < count; i++) {
            ranges.push({
                min: min + (i * stepsSize),
                max: min + ((i + 1) * stepsSize),
                color: ColorUtils.brighten(baseColor, i * brigthen)
            });
        }
        return ranges;
    },

    getColor(ranges, value) {
        for (let i = 0; i < ranges.length; i++) {
            if (value >= ranges[i].min && value <= ranges[i].max) {
                return ranges[i].color;
            }
        }
        return '#aaaaaa';
    },

    getTimeSlices(indicator) {
        const slices = indicator.timeSlices;
        slices.sort((a, b) => {
            a = Moment(a).unix();
            b = Moment(b).unix();
            if (a < b) {
                return 1;
            }
            if (a > b) {
                return -1;
            }
            return 0;
        });
        return slices;
    }
};

export default IndicatorUtils;
